import type { MailAccountSummary } from "@/lib/types/mail";

const LEGACY_ORDER_KEY = "mail-account-order-v1";
const LEGACY_SETTINGS_KEY = "mail-account-settings-v1";
const MIGRATION_FLAG_KEY = "mail-account-server-prefs-migrated-v1";

function readLegacyOrder(): number[] {
  try {
    const raw = localStorage.getItem(LEGACY_ORDER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed
        .map((id) => (typeof id === "number" ? id : Number(id)))
        .filter((id): id is number => Number.isFinite(id) && id > 0);
    }
    if (parsed && typeof parsed === "object") {
      const userId =
        localStorage.getItem("suite_user_id") || localStorage.getItem("userId") || "";
      const order = (parsed as Record<string, unknown>)[userId];
      if (Array.isArray(order)) {
        return order
          .map((id) => (typeof id === "number" ? id : Number(id)))
          .filter((id): id is number => Number.isFinite(id) && id > 0);
      }
    }
  } catch {
    /* ignore */
  }
  return [];
}

function readLegacyDisplayNames(accountIds: number[]): Map<number, string> {
  const out = new Map<number, string>();
  try {
    const raw = localStorage.getItem(LEGACY_SETTINGS_KEY);
    if (!raw) return out;
    const parsed = JSON.parse(raw) as Record<string, { displayName?: unknown }>;
    for (const id of accountIds) {
      const name = parsed[String(id)]?.displayName;
      if (typeof name === "string" && name.trim()) {
        out.set(id, name.trim().slice(0, 80));
      }
    }
  } catch {
    /* ignore */
  }
  return out;
}

function clearLegacyAccountPrefKeys(): void {
  try {
    localStorage.removeItem(LEGACY_ORDER_KEY);
    const raw = localStorage.getItem(LEGACY_SETTINGS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, Record<string, unknown>>;
    let changed = false;
    for (const key of Object.keys(parsed)) {
      if (parsed[key]?.displayName != null) {
        delete parsed[key].displayName;
        changed = true;
      }
    }
    if (changed) {
      localStorage.setItem(LEGACY_SETTINGS_KEY, JSON.stringify(parsed));
    }
  } catch {
    /* ignore */
  }
}

/** Migration unique localStorage → BDD (nom affiché + ordre comptes). */
export async function migrateLegacyAccountPrefsToServer(
  accounts: MailAccountSummary[],
  api: {
    reorderMailAccounts: (accountIds: number[]) => Promise<{ ok: boolean }>;
    saveMailAccountSettings: (
      accountId: number,
      patch: { displayName?: string }
    ) => Promise<{ ok: boolean }>;
  }
): Promise<boolean> {
  if (accounts.length === 0) return false;
  try {
    if (localStorage.getItem(MIGRATION_FLAG_KEY) === "1") return false;

    const legacyOrder = readLegacyOrder();
    const accountIds = accounts.map((a) => a.id);
    const orderIds =
      legacyOrder.length > 0
        ? [
            ...legacyOrder.filter((id) => accountIds.includes(id)),
            ...accountIds.filter((id) => !legacyOrder.includes(id)),
          ]
        : [];

    if (orderIds.length > 1) {
      await api.reorderMailAccounts(orderIds);
    }

    const legacyNames = readLegacyDisplayNames(accountIds);
    for (const account of accounts) {
      const legacyName = legacyNames.get(account.id);
      if (legacyName && !account.displayName?.trim()) {
        await api.saveMailAccountSettings(account.id, { displayName: legacyName });
      }
    }

    clearLegacyAccountPrefKeys();
    localStorage.setItem(MIGRATION_FLAG_KEY, "1");
    return true;
  } catch {
    /* retry next reload */
  }
  return false;
}
